import React, { useState, useEffect } from 'react';
import {
  Search, CheckCircle, XCircle, Clock, AlertCircle, RefreshCw,
  Building2, Smartphone, DollarSign, ArrowUpRight, Copy, Check,
  User, ExternalLink, ShieldCheck, Filter, FileText, ChevronRight
} from 'lucide-react';
import axios from 'axios';
import constant from '../constants/constant';

const WithdrawalManagement = () => {
  const [withdrawals, setWithdrawals] = useState([]);
  const [stats, setStats] = useState({
    total_requests: 0,
    pending_count: 0,
    pending_amount: 0,
    paid_count: 0,
    paid_amount: 0,
    rejected_count: 0,
    rejected_amount: 0,
    total_diamonds_withdrawn: 0,
    total_coins_withdrawn: 0
  });
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('pending'); // 'all', 'pending', 'paid', 'rejected'
  const [payoutTypeFilter, setPayoutTypeFilter] = useState('all'); // 'all', 'bank', 'upi'

  // Modals state
  const [selectedWithdrawal, setSelectedWithdrawal] = useState(null);
  const [modalType, setModalType] = useState(null); // 'details', 'approve', 'reject'
  const [transactionRef, setTransactionRef] = useState('');
  const [adminNotes, setAdminNotes] = useState('');
  const [copiedField, setCopiedField] = useState(null);

  useEffect(() => {
    fetchWithdrawals();
    fetchStats();
  }, [statusFilter]);

  const getHeaders = () => ({
    headers: {
      Authorization: sessionStorage.getItem('adminToken')
    }
  });

  const fetchWithdrawals = async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${constant.API_BASE_URL}/admin/withdrawals?status=${statusFilter}`,
        getHeaders()
      );
      if (res.data.success) {
        setWithdrawals(res.data.data || []);
      }
    } catch (err) {
      console.error("Error fetching withdrawals:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const res = await axios.get(
        `${constant.API_BASE_URL}/admin/withdrawals/stats`,
        getHeaders()
      );
      if (res.data.success) {
        setStats(res.data.data || {});
      }
    } catch (err) {
      console.error("Error fetching stats:", err);
    }
  };

  const handleCopy = (text, fieldKey) => {
    navigator.clipboard.writeText(text);
    setCopiedField(fieldKey);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const openActionModal = (item, type) => {
    setSelectedWithdrawal(item);
    setModalType(type);
    setTransactionRef(item.transaction_ref || '');
    setAdminNotes(item.admin_notes || '');
  };

  const closeModal = () => {
    setSelectedWithdrawal(null);
    setModalType(null);
    setTransactionRef('');
    setAdminNotes('');
  };

  const handleApprove = async () => {
    if (!selectedWithdrawal) return;
    setActionLoading(true);
    try {
      const res = await axios.put(
        `${constant.API_BASE_URL}/admin/withdrawals/${selectedWithdrawal.id}/status`,
        {
          status: 'paid',
          transaction_ref: transactionRef,
          admin_notes: adminNotes || 'Payout processed successfully'
        },
        getHeaders()
      );

      if (res.data.success) {
        closeModal();
        fetchWithdrawals();
        fetchStats();
      }
    } catch (err) {
      alert(err.response?.data?.message || "Failed to approve withdrawal");
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    if (!selectedWithdrawal) return;
    if (!adminNotes.trim()) {
      alert("Please provide a reason for rejecting the withdrawal request.");
      return;
    }
    setActionLoading(true);
    try {
      const res = await axios.put(
        `${constant.API_BASE_URL}/admin/withdrawals/${selectedWithdrawal.id}/status`,
        {
          status: 'rejected',
          admin_notes: adminNotes
        },
        getHeaders()
      );

      if (res.data.success) {
        closeModal();
        fetchWithdrawals();
        fetchStats();
      }
    } catch (err) {
      alert(err.response?.data?.message || "Failed to reject withdrawal");
    } finally {
      setActionLoading(false);
    }
  };

  const filteredWithdrawals = withdrawals.filter(item => {
    const term = searchTerm.toLowerCase();
    const matchesSearch = !term ||
      (item.user_name && item.user_name.toLowerCase().includes(term)) ||
      (item.email && item.email.toLowerCase().includes(term)) ||
      (item.xoxo_id && item.xoxo_id.toLowerCase().includes(term)) ||
      (item.account_holder_name && item.account_holder_name.toLowerCase().includes(term)) ||
      (item.account_number && item.account_number.toLowerCase().includes(term)) ||
      (item.ifsc_code && item.ifsc_code.toLowerCase().includes(term)) ||
      (item.upi_id && item.upi_id.toLowerCase().includes(term)) ||
      (item.transaction_ref && item.transaction_ref.toLowerCase().includes(term));

    const matchesPayoutType = payoutTypeFilter === 'all' || item.payout_type === payoutTypeFilter;
    return matchesSearch && matchesPayoutType;
  });

  return (
    <div className="space-y-6 animate-in fade-in duration-300">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-3xl font-extrabold text-slate-800 flex items-center gap-3">
            <span>Withdrawal Requests</span>
            {Number(stats.pending_count) > 0 && (
              <span className="px-3 py-1 bg-amber-500/10 text-amber-600 text-sm font-bold rounded-full border border-amber-500/20">
                {stats.pending_count} Pending
              </span>
            )}
          </h2>
          <p className="text-slate-500 mt-1">
            Review user payout requests, verify bank/UPI accounts, transfer funds, and mark payouts as completed.
          </p>
        </div>
        <button
          onClick={() => {
            fetchWithdrawals();
            fetchStats();
          }}
          className="px-5 py-2.5 bg-white border border-slate-200 text-slate-700 font-semibold rounded-xl hover:bg-slate-50 transition active:scale-95 shadow-sm flex items-center gap-2 self-start md:self-auto"
        >
          <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
          <span>Refresh</span>
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
        <div className="bg-white p-5 rounded-[22px] border border-amber-100 shadow-sm relative overflow-hidden">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-bold text-amber-600 uppercase tracking-wider">Pending Payouts</p>
              <h3 className="text-2xl font-extrabold text-slate-800 mt-1">₹{Number(stats.pending_amount || 0).toLocaleString()}</h3>
              <p className="text-xs text-slate-400 mt-0.5">{stats.pending_count || 0} requests awaiting transfer</p>
            </div>
            <div className="w-12 h-12 rounded-2xl bg-amber-50 text-amber-600 flex items-center justify-center font-bold">
              <Clock size={24} />
            </div>
          </div>
          <div className="absolute bottom-0 left-0 right-0 h-1 bg-amber-500"></div>
        </div>

        <div className="bg-white p-5 rounded-[22px] border border-emerald-100 shadow-sm relative overflow-hidden">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-bold text-emerald-600 uppercase tracking-wider">Completed / Paid</p>
              <h3 className="text-2xl font-extrabold text-slate-800 mt-1">₹{Number(stats.paid_amount || 0).toLocaleString()}</h3>
              <p className="text-xs text-slate-400 mt-0.5">{stats.paid_count || 0} successful payouts</p>
            </div>
            <div className="w-12 h-12 rounded-2xl bg-emerald-50 text-emerald-600 flex items-center justify-center font-bold">
              <CheckCircle size={24} />
            </div>
          </div>
          <div className="absolute bottom-0 left-0 right-0 h-1 bg-emerald-500"></div>
        </div>

        <div className="bg-white p-5 rounded-[22px] border border-rose-100 shadow-sm relative overflow-hidden">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-bold text-rose-600 uppercase tracking-wider">Rejected Requests</p>
              <h3 className="text-2xl font-extrabold text-slate-800 mt-1">₹{Number(stats.rejected_amount || 0).toLocaleString()}</h3>
              <p className="text-xs text-slate-400 mt-0.5">{stats.rejected_count || 0} refunded to users</p>
            </div>
            <div className="w-12 h-12 rounded-2xl bg-rose-50 text-rose-600 flex items-center justify-center font-bold">
              <XCircle size={24} />
            </div>
          </div>
          <div className="absolute bottom-0 left-0 right-0 h-1 bg-rose-500"></div>
        </div>

        <div className="bg-white p-5 rounded-[22px] border border-cyan-100 shadow-sm relative overflow-hidden">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-bold text-cyan-600 uppercase tracking-wider">Total Volume</p>
              <h3 className="text-2xl font-extrabold text-slate-800 mt-1">
                {Number(stats.total_diamonds_withdrawn || 0).toLocaleString()}
              </h3>
              <p className="text-xs text-slate-400 mt-0.5">Total Diamonds Withdrawn</p>
            </div>
            <div className="w-12 h-12 rounded-2xl bg-cyan-50 text-cyan-600 flex items-center justify-center font-bold">
              💎
            </div>
          </div>
          <div className="absolute bottom-0 left-0 right-0 h-1 bg-cyan-500"></div>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="bg-white rounded-[24px] shadow-xl shadow-slate-200/50 border border-slate-100 overflow-hidden">
        {/* Filter Navigation Tabs */}
        <div className="p-4 sm:p-6 border-b border-slate-100 flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          <div className="flex flex-wrap border-b border-slate-200 lg:border-none gap-2 sm:gap-4">
            <button
              onClick={() => setStatusFilter('pending')}
              className={`pb-3 sm:pb-4 text-sm font-bold border-b-2 transition flex items-center gap-2 ${
                statusFilter === 'pending'
                  ? 'border-amber-500 text-amber-600'
                  : 'border-transparent text-slate-400 hover:text-slate-600'
              }`}
            >
              <Clock size={16} />
              <span>Pending Requests ({stats.pending_count || 0})</span>
              {Number(stats.pending_count) > 0 && (
                <span className="w-2 h-2 rounded-full bg-amber-500 animate-ping"></span>
              )}
            </button>
            <button
              onClick={() => setStatusFilter('paid')}
              className={`pb-3 sm:pb-4 text-sm font-bold border-b-2 transition flex items-center gap-2 ${
                statusFilter === 'paid'
                  ? 'border-emerald-600 text-emerald-600'
                  : 'border-transparent text-slate-400 hover:text-slate-600'
              }`}
            >
              <CheckCircle size={16} />
              <span>Paid / Completed ({stats.paid_count || 0})</span>
            </button>
            <button
              onClick={() => setStatusFilter('rejected')}
              className={`pb-3 sm:pb-4 text-sm font-bold border-b-2 transition flex items-center gap-2 ${
                statusFilter === 'rejected'
                  ? 'border-rose-600 text-rose-600'
                  : 'border-transparent text-slate-400 hover:text-slate-600'
              }`}
            >
              <XCircle size={16} />
              <span>Rejected ({stats.rejected_count || 0})</span>
            </button>
            <button
              onClick={() => setStatusFilter('all')}
              className={`pb-3 sm:pb-4 text-sm font-bold border-b-2 transition flex items-center gap-2 ${
                statusFilter === 'all'
                  ? 'border-pink-600 text-pink-600'
                  : 'border-transparent text-slate-400 hover:text-slate-600'
              }`}
            >
              <FileText size={16} />
              <span>All ({stats.total_requests || 0})</span>
            </button>
          </div>

          {/* Search & Payout Type Controls */}
          <div className="flex flex-col sm:flex-row items-center gap-3">
            <div className="relative w-full sm:w-64">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" size={17} />
              <input
                type="text"
                placeholder="Search user, UPI, A/C, UTR..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-pink-500/20 focus:border-pink-500 transition"
              />
            </div>
            <select
              value={payoutTypeFilter}
              onChange={(e) => setPayoutTypeFilter(e.target.value)}
              className="w-full sm:w-auto px-3.5 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm font-medium text-slate-700 focus:outline-none focus:ring-2 focus:ring-pink-500/20"
            >
              <option value="all">All Payout Methods</option>
              <option value="bank">Bank Account</option>
              <option value="upi">UPI ID</option>
            </select>
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-slate-50/75 border-b border-slate-100 text-[11px] font-extrabold uppercase tracking-wider text-slate-400">
                <th className="py-4 px-6">User / Requester</th>
                <th className="py-4 px-6">Payout Amount</th>
                <th className="py-4 px-6">Payment Destination</th>
                <th className="py-4 px-6">Requested At</th>
                <th className="py-4 px-6">Status</th>
                <th className="py-4 px-6 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 text-sm">
              {loading ? (
                <tr>
                  <td colSpan="6" className="py-12 text-center text-slate-400">
                    <RefreshCw className="animate-spin inline mr-2" size={18} />
                    Loading withdrawal requests...
                  </td>
                </tr>
              ) : filteredWithdrawals.length === 0 ? (
                <tr>
                  <td colSpan="6" className="py-16 text-center text-slate-400">
                    <div className="w-16 h-16 rounded-full bg-slate-50 text-slate-300 flex items-center justify-center mx-auto mb-3">
                      <FileText size={28} />
                    </div>
                    <p className="font-semibold text-slate-600">No withdrawal requests found</p>
                    <p className="text-xs text-slate-400 mt-1">Try changing filters or search terms</p>
                  </td>
                </tr>
              ) : (
                filteredWithdrawals.map((item) => {
                  const isBank = item.payout_type === 'bank';
                  return (
                    <tr key={item.id} className="hover:bg-slate-50/70 transition">
                      {/* User Column */}
                      <td className="py-4 px-6">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-gradient-to-tr from-purple-500 to-pink-500 text-white flex items-center justify-center font-bold text-sm overflow-hidden shrink-0 shadow-sm">
                            {item.profile_pic ? (
                              <img src={item.profile_pic} alt="" className="w-full h-full object-cover" />
                            ) : (
                              (item.user_name || 'U').charAt(0).toUpperCase()
                            )}
                          </div>
                          <div>
                            <div className="font-bold text-slate-800">{item.user_name || 'Anonymous User'}</div>
                            <div className="text-xs text-slate-400 flex items-center gap-2">
                              <span>ID: <b className="text-slate-600">{item.xoxo_id || item.user_id?.substring(0, 8)}</b></span>
                              {item.phone_number && <span>• {item.phone_number}</span>}
                            </div>
                          </div>
                        </div>
                      </td>

                      {/* Amount Column */}
                      <td className="py-4 px-6">
                        <div className="font-extrabold text-slate-900 text-base">
                          ₹{Number(item.amount_inr || 0).toLocaleString()}
                        </div>
                        <div className="text-xs text-slate-500 flex items-center gap-1.5 mt-0.5">
                          {Number(item.diamonds) > 0 && (
                            <span className="text-cyan-600 font-semibold">💎 {Number(item.diamonds).toLocaleString()}</span>
                          )}
                          {Number(item.coins) > 0 && (
                            <span className="text-amber-600 font-semibold">🪙 {Number(item.coins).toLocaleString()}</span>
                          )}
                        </div>
                      </td>

                      {/* Payment Destination Column */}
                      <td className="py-4 px-6">
                        {isBank ? (
                          <div className="space-y-1">
                            <div className="flex items-center gap-2">
                              <span className="px-2 py-0.5 bg-blue-50 text-blue-700 text-[11px] font-bold rounded-md flex items-center gap-1">
                                <Building2 size={12} /> Bank
                              </span>
                              <span className="font-semibold text-slate-800 text-xs">
                                {item.account_holder_name}
                              </span>
                            </div>
                            <div className="text-xs text-slate-600 flex items-center gap-2 font-mono">
                              <span>A/C: {item.account_number}</span>
                              <button
                                onClick={() => handleCopy(item.account_number, `acc_${item.id}`)}
                                className="text-slate-400 hover:text-slate-700 transition"
                                title="Copy Account Number"
                              >
                                {copiedField === `acc_${item.id}` ? (
                                  <Check size={13} className="text-emerald-500" />
                                ) : (
                                  <Copy size={13} />
                                )}
                              </button>
                            </div>
                            <div className="text-[11px] text-slate-400 font-mono">
                              IFSC: {item.ifsc_code}
                            </div>
                          </div>
                        ) : (
                          <div className="space-y-1">
                            <div className="flex items-center gap-2">
                              <span className="px-2 py-0.5 bg-purple-50 text-purple-700 text-[11px] font-bold rounded-md flex items-center gap-1">
                                <Smartphone size={12} /> UPI
                              </span>
                              {item.account_holder_name && (
                                <span className="font-semibold text-slate-800 text-xs">
                                  {item.account_holder_name}
                                </span>
                              )}
                            </div>
                            <div className="text-xs text-slate-700 font-mono flex items-center gap-2">
                              <span className="font-bold text-purple-900">{item.upi_id}</span>
                              <button
                                onClick={() => handleCopy(item.upi_id, `upi_${item.id}`)}
                                className="text-slate-400 hover:text-slate-700 transition"
                                title="Copy UPI ID"
                              >
                                {copiedField === `upi_${item.id}` ? (
                                  <Check size={13} className="text-emerald-500" />
                                ) : (
                                  <Copy size={13} />
                                )}
                              </button>
                            </div>
                          </div>
                        )}
                      </td>

                      {/* Date Column */}
                      <td className="py-4 px-6">
                        <div className="text-xs text-slate-600 font-medium">
                          {new Date(item.created_at).toLocaleDateString('en-IN', {
                            day: 'numeric',
                            month: 'short',
                            year: 'numeric'
                          })}
                        </div>
                        <div className="text-[11px] text-slate-400">
                          {new Date(item.created_at).toLocaleTimeString('en-IN', {
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </div>
                      </td>

                      {/* Status Column */}
                      <td className="py-4 px-6">
                        {item.status === 'pending' && (
                          <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-amber-50 text-amber-700 border border-amber-200 text-xs font-bold rounded-full">
                            <Clock size={12} /> Pending Payout
                          </span>
                        )}
                        {(item.status === 'paid' || item.status === 'approved') && (
                          <div className="space-y-0.5">
                            <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-emerald-50 text-emerald-700 border border-emerald-200 text-xs font-bold rounded-full">
                              <CheckCircle size={12} /> Paid
                            </span>
                            {item.transaction_ref && (
                              <div className="text-[10px] text-slate-400 font-mono truncate max-w-[130px]" title={item.transaction_ref}>
                                Ref: {item.transaction_ref}
                              </div>
                            )}
                          </div>
                        )}
                        {item.status === 'rejected' && (
                          <div className="space-y-0.5">
                            <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-rose-50 text-rose-700 border border-rose-200 text-xs font-bold rounded-full">
                              <XCircle size={12} /> Rejected (Refunded)
                            </span>
                            {item.admin_notes && (
                              <div className="text-[10px] text-rose-400 truncate max-w-[140px]" title={item.admin_notes}>
                                {item.admin_notes}
                              </div>
                            )}
                          </div>
                        )}
                      </td>

                      {/* Actions Column */}
                      <td className="py-4 px-6 text-right">
                        <div className="flex items-center justify-end gap-2">
                          {item.status === 'pending' ? (
                            <>
                              <button
                                onClick={() => openActionModal(item, 'approve')}
                                className="px-3.5 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold rounded-xl shadow-sm hover:shadow transition active:scale-95 flex items-center gap-1"
                              >
                                <CheckCircle size={14} />
                                <span>Mark Paid</span>
                              </button>
                              <button
                                onClick={() => openActionModal(item, 'reject')}
                                className="px-3 py-1.5 bg-rose-50 hover:bg-rose-100 text-rose-600 border border-rose-200 text-xs font-bold rounded-xl transition active:scale-95 flex items-center gap-1"
                              >
                                <XCircle size={14} />
                                <span>Reject</span>
                              </button>
                            </>
                          ) : (
                            <button
                              onClick={() => openActionModal(item, 'details')}
                              className="px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-semibold rounded-xl transition flex items-center gap-1"
                            >
                              <span>View Details</span>
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ========================================================================= */}
      {/* APPROVE / MARK PAID MODAL */}
      {/* ========================================================================= */}
      {modalType === 'approve' && selectedWithdrawal && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-xs z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-[24px] shadow-2xl max-w-lg w-full p-6 space-y-5 animate-in zoom-in-95 duration-200">
            <div className="flex items-center justify-between pb-3 border-b border-slate-100">
              <div className="flex items-center gap-2.5 text-emerald-600">
                <div className="w-9 h-9 rounded-xl bg-emerald-50 flex items-center justify-center font-bold">
                  <CheckCircle size={20} />
                </div>
                <div>
                  <h3 className="font-extrabold text-slate-800 text-lg">Confirm Payout Completion</h3>
                  <p className="text-xs text-slate-400">Mark this withdrawal request as Paid</p>
                </div>
              </div>
              <button onClick={closeModal} className="text-slate-400 hover:text-slate-600 p-1">
                <XCircle size={20} />
              </button>
            </div>

            {/* Payout Summary Card */}
            <div className="p-4 bg-slate-50 rounded-2xl border border-slate-100 space-y-2.5 text-sm">
              <div className="flex justify-between items-center">
                <span className="text-slate-500">Recipient:</span>
                <span className="font-bold text-slate-800">{selectedWithdrawal.user_name} ({selectedWithdrawal.email})</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-slate-500">Amount to Transfer:</span>
                <span className="font-extrabold text-emerald-600 text-lg">₹{Number(selectedWithdrawal.amount_inr).toLocaleString()}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-slate-500">Payment Mode:</span>
                <span className="font-semibold text-slate-800 uppercase">{selectedWithdrawal.payout_type}</span>
              </div>
              {selectedWithdrawal.payout_type === 'bank' ? (
                <>
                  <div className="flex justify-between items-center">
                    <span className="text-slate-500">A/C Holder:</span>
                    <span className="font-mono text-slate-800 font-semibold">{selectedWithdrawal.account_holder_name}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-slate-500">Account No:</span>
                    <span className="font-mono text-slate-800 font-bold">{selectedWithdrawal.account_number}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-slate-500">IFSC Code:</span>
                    <span className="font-mono text-slate-800">{selectedWithdrawal.ifsc_code}</span>
                  </div>
                </>
              ) : (
                <div className="flex justify-between items-center">
                  <span className="text-slate-500">UPI ID:</span>
                  <span className="font-mono text-purple-700 font-bold">{selectedWithdrawal.upi_id}</span>
                </div>
              )}
            </div>

            <div className="space-y-3">
              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase mb-1">
                  Bank Reference / UTR Number (Optional)
                </label>
                <input
                  type="text"
                  placeholder="e.g. UTR123456789 / IMPS Ref"
                  value={transactionRef}
                  onChange={(e) => setTransactionRef(e.target.value)}
                  className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase mb-1">
                  Admin Note / Remarks
                </label>
                <input
                  type="text"
                  placeholder="e.g. Sent via NetBanking on 14 Aug"
                  value={adminNotes}
                  onChange={(e) => setAdminNotes(e.target.value)}
                  className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
                />
              </div>
            </div>

            <div className="flex gap-3 pt-2">
              <button
                onClick={closeModal}
                className="flex-1 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold rounded-xl text-sm transition"
              >
                Cancel
              </button>
              <button
                onClick={handleApprove}
                disabled={actionLoading}
                className="flex-1 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-xl text-sm shadow-md hover:shadow-lg transition active:scale-95 disabled:opacity-50"
              >
                {actionLoading ? "Processing..." : "Confirm Paid"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* REJECT MODAL */}
      {/* ========================================================================= */}
      {modalType === 'reject' && selectedWithdrawal && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-xs z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-[24px] shadow-2xl max-w-lg w-full p-6 space-y-5 animate-in zoom-in-95 duration-200">
            <div className="flex items-center justify-between pb-3 border-b border-slate-100">
              <div className="flex items-center gap-2.5 text-rose-600">
                <div className="w-9 h-9 rounded-xl bg-rose-50 flex items-center justify-center font-bold">
                  <XCircle size={20} />
                </div>
                <div>
                  <h3 className="font-extrabold text-slate-800 text-lg">Reject Withdrawal Request</h3>
                  <p className="text-xs text-slate-400">Refunds diamonds/coins automatically back to user</p>
                </div>
              </div>
              <button onClick={closeModal} className="text-slate-400 hover:text-slate-600 p-1">
                <XCircle size={20} />
              </button>
            </div>

            <div className="p-4 bg-rose-50/50 rounded-2xl border border-rose-100 space-y-2 text-sm text-slate-700">
              <p className="font-semibold text-rose-800">
                Are you sure you want to reject this payout request of ₹{Number(selectedWithdrawal.amount_inr).toLocaleString()}?
              </p>
              <p className="text-xs text-rose-600">
                ⚠️ The user's deducted balance (💎 {Number(selectedWithdrawal.diamonds).toLocaleString()} Diamonds / 🪙 {Number(selectedWithdrawal.coins).toLocaleString()} Coins) will be <b>automatically refunded</b> to their wallet.
              </p>
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-700 uppercase mb-1">
                Rejection Reason (Required) <span className="text-rose-500">*</span>
              </label>
              <textarea
                rows={3}
                placeholder="e.g. Invalid bank account details / IFSC mismatch. Please update and re-apply."
                value={adminNotes}
                onChange={(e) => setAdminNotes(e.target.value)}
                className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-rose-500/20 focus:border-rose-500"
              />
            </div>

            <div className="flex gap-3 pt-2">
              <button
                onClick={closeModal}
                className="flex-1 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold rounded-xl text-sm transition"
              >
                Cancel
              </button>
              <button
                onClick={handleReject}
                disabled={actionLoading}
                className="flex-1 py-2.5 bg-rose-600 hover:bg-rose-700 text-white font-bold rounded-xl text-sm shadow-md hover:shadow-lg transition active:scale-95 disabled:opacity-50"
              >
                {actionLoading ? "Processing Refund..." : "Confirm Rejection"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* DETAILS MODAL */}
      {/* ========================================================================= */}
      {modalType === 'details' && selectedWithdrawal && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-xs z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-[24px] shadow-2xl max-w-lg w-full p-6 space-y-5 animate-in zoom-in-95 duration-200">
            <div className="flex items-center justify-between pb-3 border-b border-slate-100">
              <h3 className="font-extrabold text-slate-800 text-lg">Withdrawal Details</h3>
              <button onClick={closeModal} className="text-slate-400 hover:text-slate-600 p-1">
                <XCircle size={20} />
              </button>
            </div>

            <div className="space-y-3 text-sm">
              <div className="flex justify-between py-2 border-b border-slate-100">
                <span className="text-slate-400">Request ID</span>
                <span className="font-mono text-slate-800 font-semibold">{selectedWithdrawal.id}</span>
              </div>
              <div className="flex justify-between py-2 border-b border-slate-100">
                <span className="text-slate-400">User</span>
                <span className="font-bold text-slate-800">{selectedWithdrawal.user_name} ({selectedWithdrawal.email})</span>
              </div>
              <div className="flex justify-between py-2 border-b border-slate-100">
                <span className="text-slate-400">Payout Amount</span>
                <span className="font-extrabold text-slate-900 text-base">₹{Number(selectedWithdrawal.amount_inr).toLocaleString()}</span>
              </div>
              <div className="flex justify-between py-2 border-b border-slate-100">
                <span className="text-slate-400">Diamonds / Coins</span>
                <span className="font-semibold text-slate-700">
                  💎 {Number(selectedWithdrawal.diamonds || 0).toLocaleString()} / 🪙 {Number(selectedWithdrawal.coins || 0).toLocaleString()}
                </span>
              </div>
              <div className="flex justify-between py-2 border-b border-slate-100">
                <span className="text-slate-400">Payout Mode</span>
                <span className="font-bold uppercase text-slate-800">{selectedWithdrawal.payout_type}</span>
              </div>
              {selectedWithdrawal.payout_type === 'bank' ? (
                <>
                  <div className="flex justify-between py-2 border-b border-slate-100">
                    <span className="text-slate-400">Account Holder</span>
                    <span className="font-semibold text-slate-800">{selectedWithdrawal.account_holder_name}</span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-slate-100">
                    <span className="text-slate-400">Account Number</span>
                    <span className="font-mono font-bold text-slate-800">{selectedWithdrawal.account_number}</span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-slate-100">
                    <span className="text-slate-400">IFSC Code</span>
                    <span className="font-mono text-slate-800">{selectedWithdrawal.ifsc_code}</span>
                  </div>
                </>
              ) : (
                <div className="flex justify-between py-2 border-b border-slate-100">
                  <span className="text-slate-400">UPI ID</span>
                  <span className="font-mono font-bold text-purple-800">{selectedWithdrawal.upi_id}</span>
                </div>
              )}
              <div className="flex justify-between py-2 border-b border-slate-100">
                <span className="text-slate-400">Status</span>
                <span className="font-bold uppercase text-slate-800">{selectedWithdrawal.status}</span>
              </div>
              {selectedWithdrawal.transaction_ref && (
                <div className="flex justify-between py-2 border-b border-slate-100">
                  <span className="text-slate-400">Transaction Reference</span>
                  <span className="font-mono font-semibold text-emerald-700">{selectedWithdrawal.transaction_ref}</span>
                </div>
              )}
              {selectedWithdrawal.admin_notes && (
                <div className="flex justify-between py-2 border-b border-slate-100">
                  <span className="text-slate-400">Admin Notes</span>
                  <span className="text-slate-700 italic">{selectedWithdrawal.admin_notes}</span>
                </div>
              )}
              {selectedWithdrawal.processed_by_name && (
                <div className="flex justify-between py-2 border-b border-slate-100">
                  <span className="text-slate-400">Processed By</span>
                  <span className="font-semibold text-slate-800">{selectedWithdrawal.processed_by_name}</span>
                </div>
              )}
            </div>

            <button
              onClick={closeModal}
              className="w-full py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold rounded-xl text-sm transition"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default WithdrawalManagement;
